/**
 * Zendesk Help Center URL resolver — JS mirror of App\Support\HelpCenterUrl.
 *
 * Builds `https://support.czechvignette.cz/hc/{locale}/` from a CMS language
 * record (one of `store.getters.languages` / `$__languages`) or a raw code
 * string. The Zendesk slug is derived from the language's `code` / `ref_code`,
 * so the list of supported languages comes from CMS data — no per-language
 * URL is hardcoded here.
 *
 * The only Zendesk-specific quirk we encode is English: our Help Center
 * default locale is UK English (`en-gb`), not US (`en-us`).
 */

const HELP_CENTER_BASE = 'https://support.czechvignette.cz/hc/'
const DEFAULT_LOCALE = 'en-gb'
const ENGLISH_LANGUAGE_CODE = 'en'

/**
 * Substrings that mark a CMS path / external URL as a Help Center link.
 * Kept in sync with App\Support\HelpCenterUrl::HELP_CENTER_HINTS.
 */
const HELP_CENTER_HINTS = Object.freeze([
    'helpcenter',
    'help-center',
    'help_center',
    'support.czechvignette.cz',
])

/**
 * Extract the lowercase 2-letter language code from a CMS language row
 * (`{ code: 'EN', ref_code: 'en_GB', … }`) or a raw locale string
 * (`'en'`, `'en-GB'`, `'en_US'`, …).
 *
 * @param {string|{ code?: string, ref_code?: string }|null|undefined} lang
 * @returns {string}
 */
function extractLanguageCode(lang) {
    if (lang == null) return ''

    const raw = typeof lang === 'object'
        ? (lang.code || lang.ref_code)
        : lang

    const normalized = String(raw || '').trim().toLowerCase().replace(/_/g, '-')
    if (normalized === '') return ''

    return normalized.split('-')[0]
}

/**
 * Resolve the Zendesk locale slug for a CMS language row or raw code.
 *
 * @param {string|{ code?: string, ref_code?: string }|null|undefined} lang
 * @returns {string}
 */
function normalizeLocale(lang) {
    const code = extractLanguageCode(lang)
    if (code === '' || code === ENGLISH_LANGUAGE_CODE) {
        return DEFAULT_LOCALE
    }
    return code
}

/**
 * Build the Zendesk Help Center URL for the given language.
 *
 * @param {string|{ code?: string, ref_code?: string }|null|undefined} lang
 * @returns {string}
 */
function resolveHelpCenterUrl(lang) {
    return `${HELP_CENTER_BASE}${normalizeLocale(lang)}/`
}

/**
 * Mirror of App\Support\HelpCenterUrl::isHelpCenterHref(): detects CMS paths
 * (`/help-center`, `helpcenter`, …) and already-resolved Zendesk URLs.
 * Used by Vue-rendered menus (e.g. MobileHeader) to mark Help Center links
 * as external (target=_blank + nofollow) and to swap them to the localised
 * Zendesk URL on render.
 */
function isHelpCenterHref(href) {
    if (href == null) return false
    const haystack = String(href).toLowerCase()
    return HELP_CENTER_HINTS.some((hint) => haystack.includes(hint))
}

/**
 * Vue-side counterpart of App\Support\HelpCenterUrl::menuHref(): returns
 * the localised Zendesk URL for Help Center menu items and the original
 * path otherwise. Accepts either a string code or a Vuex/CMS lang object.
 */
function resolveMenuHref(path, lang) {
    if (isHelpCenterHref(path)) {
        return resolveHelpCenterUrl(lang)
    }
    return (path && String(path).trim() !== '') ? path : '#'
}

export {
    HELP_CENTER_BASE,
    normalizeLocale,
    resolveHelpCenterUrl,
    isHelpCenterHref,
    resolveMenuHref,
}
